import { convertMoneyToMinorUnits, formatMoneyWithoutTrailingZeros } from '@theme/money-formatting';

/**
 * <dy-search-facets> — DY-driven facet sidebar + sort control for the search results page.
 *
 * Companion to <dy-search> (dy-search.js). The two are decoupled via document events:
 *   • <dy-search> emits `dy:search-response` after each page-1 query, carrying DY's
 *     `facets[]` + total + the active selection. This element renders/updates the sidebar
 *     from those facets and reflects the active filters.
 *   • On any facet/sort change this element reads the current selection from the DOM,
 *     syncs it to the URL, and dispatches `dy:search-request` with `{ filters, ranges, sort }`.
 *     <dy-search> re-queries DY in place — no native Shopify section morph.
 *
 * The sort control is SSR'd (dy-search-controls.liquid) from Shopify's localized
 * `sort_options`; this element maps each Shopify sort value to a DY `query.sortBy` via
 * {@link DySearchFacets.SORT_MAP}. Relevance (the default) maps to `null` → sortBy omitted.
 *
 * The sort bar is relocated out of this element by `collection-layout`, so change handling
 * is done through document-level event delegation keyed on the `data-dy-*` markers rather
 * than by DOM containment.
 *
 * @element dy-search-facets
 */
class DySearchFacets extends HTMLElement {
  /**
   * Shopify sort value -> DY `query.sortBy` ({ field, order }) or null for relevance.
   * `field` is a BARE feed column (not the docs' `type:number:` form — that errors W096).
   * NOTE: `best-selling` maps to a `popularity` rank that is NOT confirmed present in Mimco's
   * DY feed. It is effectively unreachable — search never offers best-selling, and the PLP hides
   * it via `hidden_sorts` (Featured/Best selling can't be honoured by Browse). Before offering
   * best-selling anywhere, verify the feed exposes a real popularity column and point this at it.
   * Tune field names as the feed is enriched, or override per-instance with `data-sort-map`.
   */
  static SORT_MAP = {
    relevance: null,
    'best-selling': { field: 'popularity', order: 'desc' },
    'price-ascending': { field: 'price', order: 'asc' },
    'price-descending': { field: 'price', order: 'desc' },
    'created-descending': { field: 'publish_time', order: 'desc' },
    'created-ascending': { field: 'publish_time', order: 'asc' },
    'title-ascending': { field: 'name', order: 'asc' },
    'title-descending': { field: 'name', order: 'desc' },
  };

  /**
   * Facet columns never shown as shopper filters (enforced server-side / not user-facing).
   * Intentionally empty — per-section hides are configured via `data-hidden-facets` on the element.
   */
  static HIDDEN_COLUMNS = new Set();

  /** Coalesce rapid facet/sort toggles into a single DY re-query. */
  static #DEBOUNCE_MS = 250;

  /** @type {Record<string, { field: string, order: 'asc' | 'desc' } | null>} */
  #sortMap = DySearchFacets.SORT_MAP;

  /**
   * @type {string} SSR-stamped per-market price feed column (`price_au` / `price_nz`), or '' for
   * markets without one. When set, the price sorts point at it (instead of the $0 base `price`) and
   * DY's returned facet for this column is relabelled to {@link #priceLabel}.
   */
  #priceColumn = '';

  /** @type {string} Localized "Price" label forced onto the market price facet's panel header. */
  #priceLabel = '';

  /**
   * @type {Set<string>} Facet columns to skip when rendering — the static base set plus any listed
   * in the `data-hidden-facets` attribute (CSV). The PLP hides `categories` (the collection scope
   * is fixed, so a category facet is redundant/non-functional there).
   */
  #hiddenColumns = DySearchFacets.HIDDEN_COLUMNS;

  /** @type {HTMLElement | null} The list container the facet panels render into (desktop sidebar). */
  #list = null;

  /** @type {HTMLElement | null} The list container inside the mobile drawer. */
  #drawerList = null;

  /** Cloned <svg> markup for facet icons, lifted from the SSR <template>s. */
  #plusSvg = '';
  #minusSvg = '';
  #caretSvg = '';

  /** Facet accordion glyph: 'plus' (swaps to minus) or 'caret' (rotates). */
  #filterIcon = 'plus';
  #checkSvg = '';
  #closeSvg = '';

  /** @type {number | undefined} Pending debounced re-query timer. */
  #debounceTimer;

  /**
   * @type {Map<string, { min: number | null, max: number | null }>} Last price-range band reported
   * to DY per range column, tracked per-bound. `#commit()` on the slider dispatches change on BOTH
   * the min and max inputs every commit, so this dedupes each bound: a bound reports only when it
   * actually changed, and moving one thumb never re-sends the other, unchanged bound. Cleared by
   * `#clearAllFilters` so a later re-applied identical band reports again.
   */
  #lastRange = new Map();

  /** Shopify money format string (e.g. "${{amount}} AUD") for the price slider labels. */
  #moneyFormat = '{{amount}}';
  /** ISO currency code for the price slider minor-unit conversion. */
  #currency = 'AUD';
  /** Accessible label for the price slider minimum thumb. */
  #labelPriceMin = 'Price minimum';
  /** Accessible label for the price slider maximum thumb. */
  #labelPriceMax = 'Price maximum';
  /** Accessible label for the "All" option in facet checkbox lists. */
  #labelAll = 'All';

  /** Template for an applied-filter pill's remove-button aria-label; `__DY_LABEL__` = the value. */
  #removeFilterTemplate = '';

  /** @type {(event: Event) => void} */
  #onChange = (event) => this.#handleChange(event);

  /** @type {(event: KeyboardEvent) => void} */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    for (const details of document.querySelectorAll('[data-dy-sort-details][open]')) {
      // Return focus to the disclosure trigger when focus was inside the panel being closed, so it
      // never strands on a now-hidden radio or falls to <body>. Leave focus alone when Escape was
      // pressed from elsewhere on the page.
      const restore = details.contains(document.activeElement);
      details.removeAttribute('open');
      if (restore) /** @type {HTMLElement | null} */ (details.querySelector('summary'))?.focus();
    }
  };

  /** @type {(event: Event) => void} */
  #onResponse = (event) => {
    if (event instanceof CustomEvent) this.#render(event.detail);
  };

  /** Clear all active facet filters and re-query — wired to the drawer "Clear all" button. */
  #onDrawerClearAll = () => {
    this.#clearAllFilters();
  };

  /**
   * Clear every active facet selection and price range in the DOM, then re-query. Shared by the
   * drawer "Clear all" button and the applied-pills "Clear all" link so the two entry points can't
   * drift: both must uncheck every value AND re-check/disable every "All" checkbox in BOTH lists
   * (desktop sidebar + mobile drawer render duplicate copies), not just the visible one — otherwise
   * the untouched list keeps its "All" showing stale state until the DY response rebuilds it.
   */
  #clearAllFilters() {
    for (const input of document.querySelectorAll('[data-dy-facet]:checked')) {
      if (input instanceof HTMLInputElement) input.checked = false;
    }
    for (const allInput of document.querySelectorAll('input[data-dy-facet-all]')) {
      if (!(allInput instanceof HTMLInputElement)) continue;
      allInput.checked = true;
      allInput.disabled = true;
    }
    for (const input of document.querySelectorAll('[data-dy-range-min], [data-dy-range-max]')) {
      if (input instanceof HTMLInputElement) input.value = '';
    }
    this.#resetRangeSliders();
    // Forget every reported band: clear-all resets the range inputs without a change event (it sets
    // .value directly and rebuilds the slider), so the dedupe map would otherwise keep stale bounds
    // and swallow an identical band the shopper re-applies afterwards.
    this.#lastRange.clear();
    this.#dispatchRequest();
  }

  /** Replace every price slider with a fresh instance so thumbs reset to 0%–100%. */
  #resetRangeSliders() {
    for (const slider of this.querySelectorAll('price-range-slider-component')) {
      const rangeMax = slider.dataset.rangeMax ?? '0';
      const column = slider.closest('[data-dy-range]')?.dataset.dyRange ?? 'price';
      slider.replaceWith(this.#buildSlider({ column, max: Number(rangeMax) }));
    }
  }

  /** Reads dataset config, wires event listeners, and reflects any URL filter/sort state. */
  connectedCallback() {
    this.#list = this.querySelector('[data-dy-facets-list]');
    this.#drawerList = this.querySelector('[data-dy-drawer-facets-list]');
    this.#plusSvg = this.querySelector('[data-dy-icon="plus"]')?.innerHTML ?? '';
    this.#minusSvg = this.querySelector('[data-dy-icon="minus"]')?.innerHTML ?? '';
    this.#caretSvg = this.querySelector('[data-dy-icon="caret"]')?.innerHTML ?? '';
    // Fall back to the plus variant wholesale when the caret template is absent, rather than
    // rendering an empty toggle. Falling back at the style level (not just the SVG) keeps the
    // class name and the glyph in step — a caret class around a plus would rotate the wrong shape.
    this.#filterIcon = this.dataset.filterIcon === 'caret' && this.#caretSvg ? 'caret' : 'plus';
    this.#checkSvg = this.querySelector('[data-dy-icon="checkmark"]')?.innerHTML ?? '';
    this.#closeSvg = this.querySelector('[data-dy-icon="close"]')?.innerHTML ?? '';

    this.#moneyFormat = this.dataset.moneyFormat ?? '{{amount}}';
    this.#currency = this.dataset.currency ?? 'AUD';
    this.#labelPriceMin = this.dataset.labelPriceMin ?? 'Price minimum';
    this.#labelPriceMax = this.dataset.labelPriceMax ?? 'Price maximum';
    this.#labelAll = this.dataset.labelAll ?? 'All';
    this.#removeFilterTemplate = this.dataset.removeFilterTemplate ?? '';

    this.querySelector('[data-dy-drawer-clear-all]')?.addEventListener('click', this.#onDrawerClearAll);

    // Rebuild the hidden-column set each connect: the static base plus any per-instance
    // data-hidden-facets (CSV), so a reconnect doesn't compound onto a previous merge.
    this.#hiddenColumns = new Set(DySearchFacets.HIDDEN_COLUMNS);
    for (const column of (this.dataset.hiddenFacets ?? '').split(',')) {
      const trimmed = column.trim();
      if (trimmed) this.#hiddenColumns.add(trimmed);
    }

    // Reset to the static default before applying any override, so a reconnect with a
    // different data-sort-map doesn't compound onto the previous merge.
    this.#sortMap = DySearchFacets.SORT_MAP;
    const override = this.dataset.sortMap;
    if (override) {
      try {
        this.#sortMap = { ...DySearchFacets.SORT_MAP, ...JSON.parse(override) };
      } catch {
        /* keep default map */
      }
    }

    // Repoint the price sorts at the SSR-stamped market column (the prefixed `type:number:price_au` /
    // `type:number:price_nz` — DY registers these numeric columns under their literal feed name) so DY
    // sorts on the shopper's market price, not the $0 base `price`. LIVE-VALIDATED 2026-08-27: sortBy on
    // `type:number:price_au` reorders with no W096; the bare name is silently ignored. This value also
    // matches the market facet's `column` in #render so it can be relabelled "Price". Spread into a fresh object so we never
    // mutate the shared static SORT_MAP. The price RANGE filter needs no equivalent remap here: the
    // slider auto-keys to whatever column DY returns the facet under (the market column), and the
    // non-market/base price facets are hidden via data-hidden-facets. Empty for markets without a
    // column, leaving the base-price sort in place.
    this.#priceColumn = this.dataset.priceColumn ?? '';
    this.#priceLabel = this.dataset.priceLabel ?? '';
    if (this.#priceColumn) {
      this.#sortMap = {
        ...this.#sortMap,
        'price-ascending': { field: this.#priceColumn, order: 'asc' },
        'price-descending': { field: this.#priceColumn, order: 'desc' },
      };
    }

    document.addEventListener('change', this.#onChange);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('dy:search-response', this.#onResponse);

    // Reflect any sort/filter state carried in the URL, then — if it is non-default —
    // ask <dy-search> to re-query with it (deferred so <dy-search> has bound its listener).
    // NOTE: we dispatch the URL state directly here, NOT via #dispatchRequest(), because
    // #dispatchRequest reads from the DOM (facet checkboxes/sliders) which haven't been built
    // yet on first load. Reading an empty DOM would clear the URL params and fire a filter-free
    // query. The facet panels are built later from the dy:search-response, at which point any
    // subsequent user interaction correctly goes through #dispatchRequest → #readDom.
    const active = this.#readUrl();
    // Guard against a URL sort value not offered on this page (e.g. a hand-crafted ?dysort=manual on
    // the PLP, where manual/best-selling are hidden): if no radio renders it, fall back to relevance
    // so we never dispatch a sort with no checked control to reflect or unset it.
    if (active.sortValue !== 'relevance'
      && !document.querySelector(`[data-dy-sort-input][value="${CSS.escape(active.sortValue)}"]`)) {
      active.sortValue = 'relevance';
    }
    this.#reflectSort(active.sortValue);
    // Dispatch whenever the grid element decided to skip its own initial fill — i.e. exactly when
    // #urlHasSelection() (which mirrors dy-plp/dy-search's #hasUrlSelection) is true. Gating on the
    // RAW url rather than the normalized `active` is deliberate: a URL whose only param is a
    // hidden/invalid sort (reset to relevance above, so `active` looks empty) still made the grid
    // skip, so we must answer with a request or the grid strands on skeleton placeholders forever.
    // In that normalized-away case we dispatch an empty selection, which paints the default view.
    if (this.#urlHasSelection()) {
      requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('dy:search-request', {
          detail: {
            filters: active.filters,
            ranges: active.ranges,
            sort: this.#sortMap[active.sortValue] ?? null,
          },
        }));
      });
    }
  }

  /**
   * True when the URL carries a selection the grid element treats as restore-driven (a `dyf_*` term
   * filter, a `dyfr_*` range, or a non-relevance `dysort`). MUST mirror dy-plp/dy-search's
   * `#hasUrlSelection` exactly and read the raw URL — when it is true those elements skip their own
   * initial fill and wait for the `dy:search-request` this component dispatches, so the two must
   * agree on the same raw-URL condition or the grid deadlocks (skips a fill that never gets answered).
   * @returns {boolean}
   */
  #urlHasSelection() {
    const params = new URLSearchParams(window.location.search);
    for (const [key, raw] of params) {
      if (key === 'dysort') {
        if (raw && raw !== 'relevance') return true;
      } else if (key.startsWith('dyf_')) {
        if (raw.split(',').some((value) => value.trim())) return true;
      } else if (key.startsWith('dyfr_')) {
        const [min, max] = raw.split(':').map(Number);
        if (Number.isFinite(min) && Number.isFinite(max)) return true;
      }
    }
    return false;
  }

  /** Removes event listeners and cancels any pending debounced re-query. */
  disconnectedCallback() {
    clearTimeout(this.#debounceTimer);
    this.querySelector('[data-dy-drawer-clear-all]')?.removeEventListener('click', this.#onDrawerClearAll);
    document.removeEventListener('change', this.#onChange);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('dy:search-response', this.#onResponse);
  }

  /**
   * Delegated change handler for sort radios, facet checkboxes and range inputs.
   * @param {Event} event
   */
  #handleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const sortInput = target.closest('[data-dy-sort-input]');
    if (sortInput instanceof HTMLInputElement) {
      this.#reflectSort(sortInput.value);
      // Close the sort dropdown once a choice is made, returning focus from the just-used radio to
      // the disclosure trigger so it doesn't strand on a now-hidden control.
      const details = sortInput.closest('[data-dy-sort-details]');
      details?.removeAttribute('open');
      /** @type {HTMLElement | null} */ (details?.querySelector('summary'))?.focus();
      // Report the sort change to DY. Relevance (the default) maps to null in SORT_MAP — no sort
      // criterion to report — so it's skipped and the shopper reverts to an unsorted list.
      const sort = this.#sortMap[sortInput.value];
      if (sort) {
        this.#reportDyEvent('Sort Items', {
          dyType: 'sort-items-v1',
          sortBy: sort.field.toUpperCase(),
          sortOrder: sort.order.toUpperCase(),
        });
      }
      this.#dispatchRequest();
      return;
    }

    const facetInput = target.closest('[data-dy-facet]');
    if (facetInput instanceof HTMLInputElement) {
      const column = facetInput.dataset.field;
      if (column) {
        // Report a newly-applied facet to DY. Fire only on check — filter-items-v1 models "applied a
        // filter", not removing one, so an uncheck (or the "All"/clear paths) sends nothing. The DY
        // catalog column is `filterType`; the picked value is `filterStringValue`. This runs once per
        // genuine toggle: the duplicate `.checked =` sync below fires no change event.
        // Every checkbox facet is a DY *term* column (`valuesType !== 'number'`; the only numeric
        // column is Price, which renders as the range slider — see #render). So the value is always a
        // string — `filterStringValue`, never `filterNumericValue` — even when it reads like a number
        // (size "6" is a string in the catalog, alongside "XS"/"8").
        if (facetInput.checked && facetInput.value) {
          this.#reportDyEvent('Filter Items', {
            dyType: 'filter-items-v1',
            filterType: column,
            filterStringValue: facetInput.value,
          });
        }
        // The desktop sidebar and mobile drawer each render their own copy of this checkbox, and
        // #readDom() reads both un-scoped. Mirror this toggle onto every duplicate of the same
        // value so the untouched copy can't keep the value active — otherwise unticking the visible
        // one leaves the drawer copy checked, #readDom still sees the filter, and the pill plus the
        // narrowed sibling facets stay stuck. (Programmatic .checked fires no change event, so this
        // delegated handler doesn't recurse.)
        for (const dup of document.querySelectorAll(`input[data-dy-facet][data-field="${CSS.escape(column)}"][value="${CSS.escape(facetInput.value)}"]`)) {
          if (dup instanceof HTMLInputElement) dup.checked = facetInput.checked;
        }
        // Sync "All" for this column (in BOTH lists, matching the value sync above and the pill ×
        // handler): checked/disabled only when no value is selected. A singular query would leave
        // the other list's "All" showing stale state.
        const anyChecked = !!document.querySelector(`input[data-dy-facet][data-field="${CSS.escape(column)}"]:checked`);
        for (const allInput of document.querySelectorAll(`input[data-dy-facet-all="${CSS.escape(column)}"]`)) {
          if (!(allInput instanceof HTMLInputElement)) continue;
          allInput.checked = !anyChecked;
          allInput.disabled = !anyChecked;
        }
      }
      this.#dispatchRequest();
      return;
    }

    const rangeGroup = target.closest('[data-dy-range]');
    if (rangeGroup instanceof HTMLElement) {
      this.#syncRangeDuplicates(rangeGroup);
      this.#reportRangeFilter(rangeGroup);
      this.#dispatchRequest();
    }
  }

  /**
   * The desktop sidebar and mobile drawer each render their own `<price-range-slider-component>`
   * for the same price facet. Mirror a committed change from one onto the other so both hidden
   * input pairs agree — otherwise `#readDom()` (which reads every `[data-dy-range]` group for the
   * column) would let the untouched, stale duplicate clobber the fresh value.
   * @param {HTMLElement} group - The `[data-dy-range]` group whose inputs just changed.
   */
  #syncRangeDuplicates(group) {
    const column = group.dataset.dyRange;
    if (!column) return;

    const minValue = /** @type {HTMLInputElement | null} */ (group.querySelector('[data-dy-range-min]'))?.value ?? '';
    const maxValue = /** @type {HTMLInputElement | null} */ (group.querySelector('[data-dy-range-max]'))?.value ?? '';

    for (const other of document.querySelectorAll(`[data-dy-range="${CSS.escape(column)}"]`)) {
      if (other === group) continue;

      const minInput = other.querySelector('[data-dy-range-min]');
      const maxInput = other.querySelector('[data-dy-range-max]');
      if (minInput instanceof HTMLInputElement) minInput.value = minValue;
      if (maxInput instanceof HTMLInputElement) maxInput.value = maxValue;

      const slider = other.querySelector('price-range-slider-component');
      if (slider && typeof /** @type {any} */ (slider).syncFromInputs === 'function') {
        /** @type {any} */ (slider).syncFromInputs();
      }
    }
  }

  /**
   * Read the active selection from the DOM and hand it to <dy-search> + the URL. Debounced
   * so ticking several facets in quick succession fires a single DY re-query, not one each.
   */
  #dispatchRequest() {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = window.setTimeout(() => {
      const state = this.#readDom();
      this.#syncUrl(state);
      document.dispatchEvent(new CustomEvent('dy:search-request', { detail: state }));
    }, DySearchFacets.#DEBOUNCE_MS);
  }

  /**
   * Fire a Dynamic Yield behavioral collector event, if DY's core script is present. DY owns this
   * grid, so it never sees the theme's facet/sort controls and won't auto-fire these — we emit them
   * manually. No-op when DY is absent (other brands / DY disabled), mirroring variant-picker.js's
   * `change-attr-v1` guard.
   * @param {string} name - DY event name (e.g. 'Filter Items', 'Sort Items').
   * @param {object} properties - The event `properties` payload; must include its `dyType`.
   */
  #reportDyEvent(name, properties) {
    const dy = /** @type {{ API?: (eventName: string, payload: object) => void }} */ (
      /** @type {any} */ (window).DY
    );
    if (typeof dy?.API !== 'function') return;
    // Guard the third-party call: a throw inside DY's own script must never abort the caller, which
    // goes on to mirror duplicate facet checkboxes and re-query the grid (`#dispatchRequest`).
    try {
      dy.API('event', { name, properties });
    } catch (error) {
      console.warn('[dy-search-facets] DY event failed', error);
    }
  }

  /**
   * Report a committed price-range band to DY. `filter-items-v1` has no native range shape, so a band
   * is sent as up to two NUMERIC events keyed by an invented `filterType` — `<column>-min` and
   * `<column>-max` (DY reads `filterType` as a free string) — each carrying its bound as
   * `filterNumericValue`. Only an ACTIVE bound is reported: the slider writes '' to a bound sitting at
   * its default (min 0 / max at the ceiling), so an empty input means "no constraint" and emits
   * nothing — a max-only band never sends a noise `price-min:0`. Deduped per bound via `#lastRange`
   * because `#commit()` fires change on both inputs every commit; moving one thumb must not re-send the
   * other. Values are the whole major units the slider commits — the same numbers `#readDom` queries.
   * NOTE: the `price-min`/`price-max` `filterType` strings are pending DY sign-off on how invented,
   * non-catalog filter types surface in their reporting/audiences.
   * @param {HTMLElement} group - The committed `[data-dy-range]` group.
   */
  #reportRangeFilter(group) {
    const column = group.dataset.dyRange;
    if (!column) return;

    const minEl = group.querySelector('[data-dy-range-min]');
    const maxEl = group.querySelector('[data-dy-range-max]');
    const minRaw = minEl instanceof HTMLInputElement ? minEl.value.trim() : '';
    const maxRaw = maxEl instanceof HTMLInputElement ? maxEl.value.trim() : '';

    // '' = the bound is at its default (no constraint) → not an active filter → null, never reported.
    const minVal = minRaw === '' ? null : Number(minRaw);
    const maxVal = maxRaw === '' ? null : Number(maxRaw);
    const prev = this.#lastRange.get(column) ?? { min: null, max: null };

    if (minVal !== null && Number.isFinite(minVal) && minVal !== prev.min) {
      this.#reportDyEvent('Filter Items', {
        dyType: 'filter-items-v1',
        filterType: `${column}-min`,
        filterNumericValue: minVal,
      });
    }
    if (maxVal !== null && Number.isFinite(maxVal) && maxVal !== prev.max) {
      this.#reportDyEvent('Filter Items', {
        dyType: 'filter-items-v1',
        filterType: `${column}-max`,
        filterNumericValue: maxVal,
      });
    }
    this.#lastRange.set(column, {
      min: Number.isFinite(minVal) ? minVal : null,
      max: Number.isFinite(maxVal) ? maxVal : null,
    });
  }

  /**
   * @returns {{ filters: Record<string, string[]>, ranges: Record<string, [number, number]>, sort: { field: string, order: 'asc' | 'desc' } | null }}
   */
  #readDom() {
    /** @type {Record<string, string[]>} */
    const filters = {};
    // The desktop sidebar and mobile drawer each render a checked copy of the same value (kept in
    // sync on toggle), so this un-scoped :checked query returns both. Dedupe per field/value so a
    // single selection counts once — otherwise the same value lands twice in the DY query and the
    // drawer's applied-filter count shows 2 for one pick.
    const seen = new Set();
    for (const input of document.querySelectorAll('[data-dy-facet]:checked')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const field = input.dataset.field;
      if (!field) continue;
      const key = `${field}\u0000${input.value}`;
      if (seen.has(key)) continue;
      seen.add(key);
      (filters[field] ??= []).push(input.value);
    }

    /** @type {Record<string, [number, number]>} */
    const ranges = {};
    for (const group of document.querySelectorAll('[data-dy-range]')) {
      if (!(group instanceof HTMLElement)) continue;
      const field = group.dataset.dyRange;
      if (!field) continue;
      const minEl = group.querySelector('[data-dy-range-min]');
      const maxEl = group.querySelector('[data-dy-range-max]');
      const minRaw = minEl instanceof HTMLInputElement ? minEl.value.trim() : '';
      const maxRaw = maxEl instanceof HTMLInputElement ? maxEl.value.trim() : '';
      if (minRaw === '' && maxRaw === '') continue;
      // Slider writes '' to maxInput when thumb is at the ceiling (no upper constraint).
      // Fall back to the slider's data-range-max so the DY query gets the real ceiling
      // instead of Number('') === 0.
      const slider = group.querySelector('price-range-slider-component');
      const sliderMax = slider instanceof HTMLElement ? Number(slider.dataset.rangeMax ?? '') : NaN;
      const min = minRaw === '' ? 0 : Number(minRaw);
      const max = maxRaw !== '' ? Number(maxRaw) : (Number.isFinite(sliderMax) ? sliderMax : NaN);
      if (Number.isFinite(min) && Number.isFinite(max)) ranges[field] = [min, max];
    }

    const sortValue = /** @type {HTMLInputElement | null} */ (
      document.querySelector('[data-dy-sort-input]:checked')
    )?.value ?? 'relevance';

    return { filters, ranges, sort: this.#sortMap[sortValue] ?? null };
  }

  /** Update the sort radio + option aria-selected + summary status to match `value`. */
  #reflectSort(value) {
    let statusText = '';
    for (const input of document.querySelectorAll('[data-dy-sort-input]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const isActive = input.value === value;
      input.checked = isActive;
      if (isActive) statusText = input.dataset.optionName ?? '';
    }
    const isSorted = value !== 'relevance' && value !== '';
    for (const status of document.querySelectorAll('[data-dy-sort-status]')) {
      // Only show the status label when a non-default sort is active; empty hides via
      // the :not(:empty) CSS selector so the summary reads cleanly on mobile.
      status.textContent = isSorted ? statusText : '';
    }
    for (const details of document.querySelectorAll('[data-dy-sort-details]')) {
      details.classList.toggle('facets__panel--sorted', isSorted);
    }
  }

  /**
   * Render (first response) or update (later responses) the facet panels from DY's facets[],
   * and re-render the applied-filter chips row.
   * @param {{ facets: unknown, filters?: Record<string, string[]>, ranges?: Record<string, [number, number]> }} detail
   */
  #render(detail) {
    const facets = Array.isArray(detail?.facets) ? detail.facets : [];
    const activeFilters = detail?.filters ?? {};
    const activeRanges = detail?.ranges ?? {};

    // Force the market price facet's panel header to the localized "Price" label, so a DY displayName
    // of e.g. "price_au" never surfaces to shoppers. Mutating the response facet is safe — it's a
    // fresh object per response, read by both the columnOrder pass and the panel build below.
    if (this.#priceColumn && this.#priceLabel) {
      for (const facet of facets) {
        if (facet && typeof facet === 'object' && /** @type {any} */ (facet).column === this.#priceColumn) {
          /** @type {any} */ (facet).displayName = this.#priceLabel;
        }
      }
    }

    // Canonical column order straight from DY's facets[], used to place a newly-built panel at its
    // rightful slot instead of the end. A facet skipped on an earlier response (e.g. price collapsing
    // to min==max under a narrow filter) otherwise reappears at the BOTTOM of the list once it
    // regains a spread, rather than returning to its normal position.
    const columnOrder = facets
      .filter((f) => f && typeof f === 'object' && typeof (/** @type {any} */ (f).column) === 'string')
      .map((f) => /** @type {any} */ (f).column);

    // Render facet panels into both the desktop sidebar and the mobile drawer.
    // Each gets a unique idPrefix so label[for] targets stay within their own list.
    for (const [targetList, idPrefix] of /** @type {[HTMLElement|null, string][]} */ ([
      [this.#list, 'dyf'],
      [this.#drawerList, 'dyfd'],
    ])) {
      if (!targetList) continue;
      for (const facet of facets) {
        if (!facet || typeof facet !== 'object') continue;
        const column = /** @type {any} */ (facet).column;
        if (typeof column !== 'string' || this.#hiddenColumns.has(column)) continue;

        const existing = targetList.querySelector(`[data-dy-facet-panel="${CSS.escape(column)}"]`);

        const isRange = /** @type {any} */ (facet).valuesType === 'number';
        // A range facet with no real spread (an un-priced feed reporting 0..0, or a filter narrowed
        // to a single price point) can't drive a slider. Tear down any EXISTING panel so a stale
        // range — e.g. a $0–549 slider left over from the broad set — can't linger showing the wrong
        // max; it rebuilds at its proper slot once DY reports a spread again. Skipping without this
        // teardown (the old behaviour) left the previous slider untouched and out of date.
        if (isRange && !(Number(/** @type {any} */ (facet).max) > Number(/** @type {any} */ (facet).min))) {
          if (existing) this.#teardownStalePanel(existing, targetList);
          continue;
        }

        const active = activeFilters[column] ?? [];

        // A term facet whose applicable options collapse to a single choice offers no narrowing
        // (e.g. Bags collection where every product is "One size" — ticking it doesn't shrink the
        // list). Hide the panel when the union of (values with count > 0) and (currently active
        // values) is ≤ 1. The applied-filter pill row still lets a shopper remove a stranded
        // selection when this hides a panel with an active value. Mirrors #updateTermPanel's own
        // "visible option" rule (count>0 OR checked) so what we skip building matches what it
        // would render as visible.
        if (!isRange) {
          const values = Array.isArray(/** @type {any} */ (facet).values) ? /** @type {any} */ (facet).values : [];
          const visible = new Set();
          for (const v of values) {
            const name = String(v?.name ?? '');
            if (name && Number(v?.count ?? 0) > 0) visible.add(name);
          }
          for (const name of active) if (name) visible.add(name);
          if (visible.size <= 1) {
            if (existing) this.#teardownStalePanel(existing, targetList);
            continue;
          }
        }
        if (existing) {
          if (isRange) this.#updateRangePanel(existing, facet);
          else this.#updateTermPanel(existing, facet, active, idPrefix);
          continue;
        }

        const panel = isRange
          ? this.#buildRangePanel(facet, idPrefix, activeRanges[column])
          : this.#buildTermPanel(facet, active, idPrefix);
        // Insert before the first already-rendered panel whose column comes later in DY's order so
        // the list stays in canonical order; fall back to appending when none is present yet (the
        // normal first-render path, where each panel is built in order).
        let anchor = null;
        for (const laterCol of columnOrder.slice(columnOrder.indexOf(column) + 1)) {
          const candidate = targetList.querySelector(`[data-dy-facet-panel="${CSS.escape(laterCol)}"]`);
          // Anchor on the `.facets__item` wrapper, not the inner <details>, so the new wrapper is
          // inserted as a list sibling rather than nested inside the neighbour (which would break
          // the ::before separator borders).
          if (candidate) { anchor = candidate.closest('.facets__item') ?? candidate; break; }
        }
        if (anchor) anchor.before(panel);
        else targetList.appendChild(panel);
      }
    }

    // A range panel above may have clamped its slider down to a shrunk DY bound (e.g. a shopper-set
    // $300 max when a newly-selected facet caps applicable prices at $150): price-range-slider's
    // update() rewrites the hidden inputs to match, dropping the now-meaningless upper constraint.
    // Re-read ranges from the post-clamp DOM so the applied pill and drawer count reflect the
    // slider's actual state, not the stale range echoed back from the request.
    const clampedRanges = this.#readDom().ranges;
    this.#renderApplied(facets, activeFilters, clampedRanges);
    this.#updateDrawerUI(activeFilters, clampedRanges, detail?.total ?? null);
  }

  /**
   * Update the mobile drawer UI: "See results" count, "Clear all" active state,
   * and the filter count bubble on the toggle button.
   * @param {Record<string, string[]>} activeFilters
   * @param {Record<string, [number, number]>} activeRanges
   * @param {number | null} total
   */
  #updateDrawerUI(activeFilters, activeRanges, total) {
    // "See results" button — update count from DY total.
    const seeResults = this.querySelector('[data-dy-see-results]');
    if (seeResults instanceof HTMLButtonElement && total !== null) {
      const template = seeResults.dataset.seeResultsTemplate ?? '';
      seeResults.textContent = template.replace('__DY_COUNT__', String(total));
    }

    // "Clear all" button — show only when filters are active.
    const clearAll = this.querySelector('[data-dy-drawer-clear-all]');
    if (clearAll instanceof HTMLButtonElement) {
      const hasActive = Object.keys(activeFilters).length > 0 || Object.keys(activeRanges).length > 0;
      clearAll.classList.toggle('facets__clear-all--active', hasActive);
    }

    // Filter count bubble on the toggle button.
    const bubble = this.querySelector('[data-dy-filter-count-bubble]');
    if (bubble instanceof HTMLElement) {
      const count = Object.values(activeFilters).reduce((s, v) => s + v.length, 0)
        + Object.keys(activeRanges).length;
      const text = bubble.querySelector('.filter-count-bubble__text');
      if (text) text.textContent = String(count);
      bubble.hidden = count === 0;
      // The SSR bubble starts aria-hidden (empty until filters apply); expose the live count to ATs
      // once it is shown and re-hide it when it empties, so the toggle's active-filter count is
      // perceivable rather than silently visual-only.
      if (count === 0) bubble.setAttribute('aria-hidden', 'true');
      else bubble.removeAttribute('aria-hidden');
    }
  }

  /**
   * Render or update the applied-filter chips row ([data-dy-applied-pills]).
   * Each active term filter value and range gets a removable pill. A "Clear all"
   * link appears when anything is active, matching the PLP controls bar.
   * @param {any[]} facets
   * @param {Record<string, string[]>} activeFilters
   * @param {Record<string, [number, number]>} activeRanges
   */
  #renderApplied(facets, activeFilters, activeRanges) {
    const container = document.querySelector('[data-dy-applied-pills]');
    if (!(container instanceof HTMLElement)) return;

    // Preserve keyboard focus across the teardown+rebuild below. Removing a pill moves focus to an
    // adjacent pill/clear-all optimistically, but this authoritative re-render (fired ~250ms later
    // by the DY response) wipes the row with innerHTML = '' and rebuilds it — destroying whatever
    // held focus and dropping it to <body>. If focus was inside the row, restore it to the
    // equivalent control in the freshly built row afterwards. When focus is elsewhere (e.g. a facet
    // checkbox in the sidebar), leave it alone so we never steal focus on an unrelated re-query.
    const hadFocus = container.contains(document.activeElement);

    // Build a display-name lookup from DY facets.
    /** @type {Record<string, string>} */
    const nameFor = {};
    for (const f of facets) {
      if (f && typeof f.column === 'string') nameFor[f.column] = f.displayName || f.column;
    }

    container.innerHTML = '';

    for (const [column, values] of Object.entries(activeFilters)) {
      for (const val of values) {
        container.appendChild(this.#buildPill(this.#titleCase(val), column, 'term', val));
      }
    }

    for (const [column, [min, max]] of Object.entries(activeRanges)) {
      const step = convertMoneyToMinorUnits('1', this.#currency) || 1;
      const fMin = formatMoneyWithoutTrailingZeros(min * step, this.#moneyFormat, this.#currency);
      const fMax = formatMoneyWithoutTrailingZeros(max * step, this.#moneyFormat, this.#currency);
      container.appendChild(this.#buildPill(`${fMin}–${fMax}`, column, 'range', null));
    }

    if (container.children.length > 0) {
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'button-unstyled facets__clear-all-link facets__clear-all-link--active';
      // Read the localized label from its SSR span (rendered unconditionally, editor or not); the
      // hardcoded string is only a last-resort fallback if that span is somehow missing.
      clearBtn.textContent = document.querySelector('[data-dy-clear-all-label]')?.textContent ?? 'Clear all';
      clearBtn.addEventListener('click', () => {
        // Optimistically empty the pills row so the pills and this "Clear all" disappear on click
        // rather than lingering until the DY re-query resolves and #renderApplied rebuilds it. Move
        // focus to the row first (it holds this button, about to be removed) so it doesn't fall to
        // <body>; the authoritative rebuild leaves the row empty and re-restores focus there.
        container.setAttribute('tabindex', '-1');
        container.focus();
        container.innerHTML = '';
        // Clear the facet/range DOM state (both lists) and re-query via the shared helper.
        this.#clearAllFilters();
      });
      container.appendChild(clearBtn);
    }

    if (hadFocus) {
      // Restore to the first pill's remove button, else "Clear all", else the (now-empty) row
      // itself — anywhere in the row beats letting focus fall to <body>.
      const restore = /** @type {HTMLElement | null} */ (
        container.querySelector('.facets-remove__pill button')
        ?? container.querySelector('.facets__clear-all-link')
      );
      if (restore) {
        restore.focus();
      } else {
        container.setAttribute('tabindex', '-1');
        container.focus();
      }
    }
  }

  /**
   * Build a single applied-filter pill button.
   * @param {string} label
   * @param {string} column
   * @param {'term'|'range'} type
   * @param {string|null} value
   * @returns {HTMLElement}
   */
  #buildPill(label, column, type, value) {
    const pill = document.createElement('div');
    pill.className = 'pills__pill pills__pill--desktop-small facets-remove__pill';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'facets-remove__pill-button';
    // Name the control by its action, not just the value ("Remove Blue" vs "Blue"), so ATs convey
    // that activating it removes the filter. Falls back to the visible value if no template is wired.
    if (this.#removeFilterTemplate) {
      btn.setAttribute('aria-label', this.#removeFilterTemplate.replace('__DY_LABEL__', label));
    }
    btn.addEventListener('click', () => {
      if (type === 'range') {
        for (const input of document.querySelectorAll(`[data-dy-range="${CSS.escape(column)}"] [data-dy-range-min], [data-dy-range="${CSS.escape(column)}"] [data-dy-range-max]`)) {
          if (input instanceof HTMLInputElement) input.value = '';
        }
        this.#resetRangeSliders();
      } else {
        // Uncheck EVERY duplicate of this value: the desktop sidebar and mobile drawer each render
        // their own copy and #readDom() reads both un-scoped, so unchecking only the first match
        // (e.g. when the drawer copy is the checked one) leaves the filter active and the pill
        // re-appears on the next response.
        for (const input of document.querySelectorAll(`input[data-dy-facet][data-field="${CSS.escape(column)}"][value="${CSS.escape(value ?? '')}"]`)) {
          if (input instanceof HTMLInputElement) input.checked = false;
        }
        // Reflect the "All" option (in both lists) when no values remain checked for this column.
        const anyChecked = !!document.querySelector(`input[data-dy-facet][data-field="${CSS.escape(column)}"]:checked`);
        for (const allInput of document.querySelectorAll(`input[data-dy-facet-all="${CSS.escape(column)}"]`)) {
          if (!(allInput instanceof HTMLInputElement)) continue;
          allInput.checked = !anyChecked;
          allInput.disabled = !anyChecked;
        }
      }
      // Remove the pill immediately so it disappears on click, rather than lingering until the
      // DY re-query resolves and #renderApplied rebuilds the row. That re-render is authoritative
      // and reconciles the final state; this is just optimistic feedback. Move focus to an adjacent
      // control BEFORE removing the button (which currently holds focus) so focus doesn't fall to
      // <body>. If this was the last pill, drop the now-orphaned "Clear all" too.
      const row = pill.parentElement;
      const siblings = row ? [...row.querySelectorAll('.facets-remove__pill')] : [];
      const pos = siblings.indexOf(pill);
      const nextPill = siblings[pos + 1] ?? siblings[pos - 1] ?? null;
      pill.remove();
      let focusTarget = /** @type {HTMLElement | null} */ (nextPill?.querySelector('button') ?? null);
      if (!focusTarget && row) {
        if (!row.querySelector('.facets-remove__pill')) row.querySelector('.facets__clear-all-link')?.remove();
        focusTarget = /** @type {HTMLElement | null} */ (row.querySelector('.facets__clear-all-link'));
        if (!focusTarget) {
          // Nothing left in the row — keep focus near where the pills were rather than on <body>.
          row.setAttribute('tabindex', '-1');
          focusTarget = row;
        }
      }
      focusTarget?.focus();
      this.#dispatchRequest();
    });

    const text = document.createTextNode(label);
    const iconWrap = document.createElement('span');
    iconWrap.className = 'svg-wrapper svg-wrapper--smaller';
    iconWrap.setAttribute('aria-hidden', 'true');
    iconWrap.innerHTML = this.#closeSvg;

    btn.append(text, iconWrap);
    pill.appendChild(btn);
    return pill;
  }

  /**
   * Build the shared `.facets__summary` header (label + status-wrapper + accordion toggle),
   * matching the PLP vertical facet markup so the shared facet CSS resolves identically.
   * @param {string} labelText
   * @returns {HTMLElement}
   */
  #buildSummary(labelText) {
    const summary = document.createElement('summary');
    summary.className = 'facets__summary';
    const label = document.createElement('span');
    label.className = 'facets__label';
    label.textContent = labelText;
    const statusWrap = document.createElement('div');
    statusWrap.className = 'facets__status-wrapper';
    // Toggle glyph. Both variants reuse the native filters' class names so the shared facet CSS
    // in blocks/filters.liquid drives them: .icon-plus-minus swaps plus↔minus off the parent
    // details[open] state, .icon-caret.icon-animated rotates. Decorative either way — the
    // <summary> label already names the panel, so hide the glyph from ATs.
    const icon = document.createElement('span');
    icon.setAttribute('aria-hidden', 'true');
    if (this.#filterIcon === 'caret') {
      icon.className = 'svg-wrapper icon-caret icon-animated';
      icon.innerHTML = this.#caretSvg;
    } else {
      icon.className = 'svg-wrapper icon-plus-minus';
      const plus = document.createElement('span');
      plus.className = 'icon-plus-minus__plus';
      plus.innerHTML = this.#plusSvg;
      const minus = document.createElement('span');
      minus.className = 'icon-plus-minus__minus';
      minus.innerHTML = this.#minusSvg;
      icon.append(plus, minus);
    }
    statusWrap.appendChild(icon);
    summary.append(label, statusWrap);
    return summary;
  }

  /**
   * Build a `.facets__item` panel shell (matching the PLP accordion item) with the shared
   * summary and an empty flex content column. Returns the wrapper + the list the caller fills.
   * @param {any} facet
   * @param {boolean} open
   * @returns {{ item: HTMLElement, content: HTMLElement }}
   */
  #buildPanelShell(facet, open) {
    const item = document.createElement('div');
    item.className = 'facets__item';

    const details = document.createElement('details');
    details.className = 'facets__panel';
    details.dataset.dyFacetPanel = facet.column;
    details.open = open;

    const content = document.createElement('div');
    content.className = 'facets__inputs facets__panel-content details-content';

    details.append(this.#buildSummary(facet.displayName || facet.column), content);
    item.appendChild(details);
    return { item, content };
  }

  /**
   * @param {any} facet
   * @param {string[]} active
   * @param {string} [idPrefix]
   * @returns {HTMLElement}
   */
  #buildTermPanel(facet, active, idPrefix = 'dyf') {
    const { item, content } = this.#buildPanelShell(facet, active.length > 0);

    const wrapper = document.createElement('div');
    wrapper.className = 'facets__inputs-wrapper';
    const ul = document.createElement('ul');
    ul.className = 'facets__inputs-list list-unstyled';
    // Preserve list semantics: list-unstyled sets list-style:none, which strips list role in
    // Safari/VoiceOver.
    ul.setAttribute('role', 'list');

    // "All" item — checked when nothing else is selected; acts as a clear for this facet.
    ul.appendChild(this.#buildAllCheckbox(facet.column, active.length === 0, idPrefix));

    for (const value of facet.values ?? []) {
      ul.appendChild(this.#buildCheckbox(facet.column, value, active.includes(value?.name), idPrefix));
    }
    wrapper.appendChild(ul);
    content.appendChild(wrapper);

    return item;
  }

  /**
   * Build the "All" checkbox item for a term facet panel.
   * Checked when no values are active; unchecks itself and dispatches when clicked with values.
   * @param {string} column
   * @param {boolean} checked
   * @param {string} [idPrefix]
   * @returns {HTMLLIElement}
   */
  #buildAllCheckbox(column, checked, idPrefix = 'dyf') {
    const allId = `${idPrefix}-all-${column}`.replace(/[^a-z0-9_-]+/gi, '-');
    const li = document.createElement('li');
    li.className = 'facets__inputs-list-item facets__inputs-list-item--all';

    const wrap = document.createElement('div');
    wrap.className = 'checkbox';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'checkbox__input';
    input.id = allId;
    input.setAttribute('data-dy-facet-all', column);
    input.checked = checked;
    input.disabled = checked;
    input.addEventListener('change', () => {
      // Capture focus before the disable loop below: disabling the just-used "All" checkbox blurs it
      // synchronously, so we must note it now to restore focus afterwards.
      const wasFocused = document.activeElement === input;
      // Clear all checked values for this column (across both lists) then re-query.
      for (const cb of document.querySelectorAll(`input[data-dy-facet][data-field="${CSS.escape(column)}"]:checked`)) {
        if (cb instanceof HTMLInputElement) cb.checked = false;
      }
      // Re-check + disable this column's "All" in BOTH lists (desktop sidebar + mobile drawer), not
      // just the clicked copy — otherwise the sibling "All" lingers unchecked/enabled until the DY
      // response rebuilds it.
      for (const allInput of document.querySelectorAll(`input[data-dy-facet-all="${CSS.escape(column)}"]`)) {
        if (!(allInput instanceof HTMLInputElement)) continue;
        allInput.checked = true;
        allInput.disabled = true;
      }
      // The now-disabled "All" can't hold focus; return it to the panel summary so keyboard users
      // keep their place instead of dropping to <body>.
      if (wasFocused) /** @type {HTMLElement | null} */ (input.closest('details')?.querySelector('summary'))?.focus();
      this.#dispatchRequest();
    });

    const labelEl = document.createElement('label');
    labelEl.className = 'checkbox__label';
    labelEl.htmlFor = allId;
    labelEl.innerHTML = this.#checkSvg;
    // Decorative tick — the checkbox state is conveyed by the input, so hide it from ATs.
    labelEl.querySelector('svg')?.setAttribute('aria-hidden', 'true');
    const text = document.createElement('span');
    text.className = 'checkbox__label-text';
    text.textContent = this.#labelAll;
    labelEl.appendChild(text);

    wrap.append(input, labelEl);
    li.appendChild(wrap);
    return li;
  }

  /**
   * @param {string} column
   * @param {{ name: string, count: number }} value
   * @param {boolean} checked
   * @param {string} [idPrefix]
   * @returns {HTMLLIElement}
   */
  #buildCheckbox(column, value, checked, idPrefix = 'dyf') {
    const name = String(value?.name ?? '');
    const count = Number(value?.count ?? 0);
    const disabled = count === 0 && !checked;
    const id = `${idPrefix}-${column}-${name}`.replace(/[^a-z0-9_-]+/gi, '-');

    const li = document.createElement('li');
    li.className = 'facets__inputs-list-item';
    // Hide inapplicable (zero-count, unchecked) values at build time, matching the reconciliation
    // in #updateTermPanel. A bookmarked/filtered URL fires the first dy:search-request in
    // connectedCallback, so the very first response can carry zero-count values — without this they
    // would render visible-but-disabled on first paint, then vanish on the next response (a layout
    // shift). Both paths now share one rule: zero-count + unchecked ⇒ hidden (via `hidden` +
    // input.disabled; no --disabled class, which only styles the swatch/image variant markup).
    if (disabled) li.hidden = true;

    const wrap = document.createElement('div');
    wrap.className = 'checkbox';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'checkbox__input';
    input.id = id;
    input.value = name;
    input.dataset.dyFacet = '';
    input.dataset.field = column;
    input.checked = checked;
    input.disabled = disabled;

    const label = document.createElement('label');
    label.className = 'checkbox__label';
    label.htmlFor = id;
    label.innerHTML = this.#checkSvg;
    // Decorative tick — the checkbox state is conveyed by the input, so hide it from ATs.
    label.querySelector('svg')?.setAttribute('aria-hidden', 'true');
    const text = document.createElement('span');
    text.className = 'checkbox__label-text';
    // No inline count — the PLP sidebar shows option labels only; zero-count values are
    // disabled (below) rather than annotated.
    text.textContent = this.#titleCase(name);
    label.appendChild(text);

    wrap.append(input, label);
    li.appendChild(wrap);
    return li;
  }

  /**
   * Reconcile an existing panel against a fresh DY response without rebuilding it, so an open
   * panel and the shopper's checkbox state survive a re-query. Syncs the "All" checkbox, appends
   * any newly-introduced values, hides options DY no longer lists (inapplicable under the current
   * selection), and shifts focus off an about-to-hide option so it never falls to <body>.
   * @param {Element} panel
   * @param {any} facet
   * @param {string[]} active
   * @param {string} [idPrefix]
   */
  #updateTermPanel(panel, facet, active, idPrefix = 'dyf') {
    const ul = panel.querySelector('.facets__inputs-list');
    if (!ul) return;

    // Sync "All" checkbox: checked/disabled when nothing is active.
    const allInput = ul.querySelector(`input[data-dy-facet-all]`);
    if (allInput instanceof HTMLInputElement) {
      allInput.checked = active.length === 0;
      allInput.disabled = active.length === 0;
    }

    // DY returns only the values still applicable under the OTHER active filters, and — for the
    // facet the shopper is currently filtering on — the full un-narrowed list (DY excludes a
    // facet's own selection from its own counts). So map every value DY sent, add any it newly
    // introduced, then reconcile EVERY rendered option below: a value DY no longer lists has 0
    // results under the current selection and must be disabled. Skipping that (the old behaviour)
    // left inapplicable options — e.g. colours with no acetate product — enabled and clickable.
    const counts = new Map();
    for (const value of facet.values ?? []) {
      const name = String(value?.name ?? '');
      counts.set(name, Number(value?.count ?? 0));
      if (!ul.querySelector(`input[data-dy-facet][value="${CSS.escape(name)}"]`)) {
        ul.appendChild(this.#buildCheckbox(facet.column, value, false, idPrefix));
      }
    }

    for (const input of ul.querySelectorAll('input[data-dy-facet]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      // Re-assert checked state from the authoritative per-column `active` list. A no-op in the
      // normal toggle flow (where `active` is derived from these same inputs), but it corrects any
      // drift when `active` originates elsewhere (URL restore / programmatic clear) so a rendered
      // option can't keep a stale tick.
      input.checked = active.includes(input.value);
      // Hide values DY no longer lists so the list visibly shrinks to the applicable options,
      // matching native Shopify facets (choose colour "gold" → the material list drops to just the
      // materials that come in gold). A checked value stays visible so the shopper can always
      // toggle it back off, even once DY drops it under the current selection.
      const inapplicable = (counts.get(input.value) ?? 0) === 0 && !input.checked;
      const item = input.closest('.facets__inputs-list-item');
      // Capture focus BEFORE disabling: setting input.disabled blurs a focused control synchronously,
      // so a document.activeElement check after the assignment would always miss it.
      const hadFocus = item instanceof HTMLElement && item.contains(document.activeElement);
      input.disabled = inapplicable;
      if (item instanceof HTMLElement) {
        // If focus was on an option about to be hidden (shopper tabbing an open panel when an
        // unrelated re-query drops this value), move it to the panel heading so it doesn't
        // fall to <body> mid-navigation.
        if (inapplicable && hadFocus) {
          const summary = item.closest('details')?.querySelector('summary');
          if (summary instanceof HTMLElement) summary.focus();
        }
        item.hidden = inapplicable;
      }
    }
  }

  /**
   * Builds a price panel containing a dual-thumb `<price-range-slider-component>`.
   * The slider's hidden inputs carry `data-dy-range-min` / `data-dy-range-max` so
   * `#readDom` and `#onDrawerClearAll` can locate them, and `price-range-slider.js`
   * dispatches `change` events on them after each commit so `#handleChange` triggers a re-query.
   * @param {any} facet
   * @param {string} [idPrefix]
   * @param {[number, number]} [activeRange] - The range already active from the URL, if any.
   * @returns {HTMLElement}
   */
  #buildRangePanel(facet, idPrefix = 'dyf', activeRange) {
    const { item, content } = this.#buildPanelShell(facet, !!activeRange);

    const wrapper = document.createElement('div');
    wrapper.className = 'facets__inputs-wrapper';

    const rangeGroup = document.createElement('div');
    rangeGroup.dataset.dyRange = facet.column;

    rangeGroup.appendChild(this.#buildSlider(facet, activeRange));
    wrapper.appendChild(rangeGroup);
    content.appendChild(wrapper);
    return item;
  }

  /**
   * Builds a `<price-range-slider-component>` for the given range facet.
   * @param {any} facet
   * @param {[number, number]} [activeRange] - The range already active from the URL, if any.
   * @returns {HTMLElement}
   */
  #buildSlider(facet, activeRange) {
    const rangeMax = String(facet.max ?? 0);
    const rangeMaxNumber = Number(rangeMax);
    const [rawMin, rawMax] = activeRange ?? [0, rangeMaxNumber];
    // Clamp against the facet's current bound: a bookmarked/hand-edited URL's dyfr_price
    // can be stale (e.g. the catalogue was re-priced since) or otherwise malformed, and an
    // out-of-range value here would push thumbs off the track and aria-valuenow outside
    // aria-valuemin/aria-valuemax.
    const activeMin = Math.min(Math.max(0, rawMin), rangeMaxNumber);
    const activeMax = Math.min(Math.max(activeMin, rawMax), rangeMaxNumber);
    const minPercent = (activeMin / rangeMaxNumber) * 100;
    const maxPercent = (activeMax / rangeMaxNumber) * 100;

    const slider = document.createElement('price-range-slider-component');
    slider.className = 'price-range-slider';
    slider.dataset.rangeMax = rangeMax;
    slider.dataset.currency = this.#currency;

    const tmpl = document.createElement('template');
    tmpl.setAttribute('ref', 'moneyFormat');
    tmpl.innerHTML = this.#moneyFormat;

    const labels = document.createElement('div');
    labels.className = 'price-range-slider__labels';
    labels.setAttribute('aria-hidden', 'true');
    const minLabel = document.createElement('span');
    minLabel.setAttribute('ref', 'minLabel');
    const maxLabel = document.createElement('span');
    maxLabel.setAttribute('ref', 'maxLabel');
    labels.append(minLabel, maxLabel);

    const trackWrapper = document.createElement('div');
    trackWrapper.className = 'price-range-slider__track-wrapper';
    trackWrapper.setAttribute('ref', 'trackWrapper');

    const track = document.createElement('div');
    track.className = 'price-range-slider__track';

    const fill = document.createElement('div');
    fill.className = 'price-range-slider__fill';
    fill.setAttribute('ref', 'fill');
    fill.style.cssText = `left: ${minPercent}%; width: ${maxPercent - minPercent}%;`;

    const minThumb = document.createElement('div');
    minThumb.className = 'price-range-slider__thumb price-range-slider__thumb--min';
    minThumb.setAttribute('ref', 'minThumb');
    minThumb.setAttribute('role', 'slider');
    minThumb.setAttribute('tabindex', '0');
    minThumb.setAttribute('aria-label', this.#labelPriceMin);
    minThumb.setAttribute('aria-valuemin', '0');
    minThumb.setAttribute('aria-valuemax', rangeMax);
    minThumb.setAttribute('aria-valuenow', String(activeMin));
    minThumb.style.left = `${minPercent}%`;

    const maxThumb = document.createElement('div');
    maxThumb.className = 'price-range-slider__thumb price-range-slider__thumb--max';
    maxThumb.setAttribute('ref', 'maxThumb');
    maxThumb.setAttribute('role', 'slider');
    maxThumb.setAttribute('tabindex', '0');
    maxThumb.setAttribute('aria-label', this.#labelPriceMax);
    maxThumb.setAttribute('aria-valuemin', '0');
    maxThumb.setAttribute('aria-valuemax', rangeMax);
    maxThumb.setAttribute('aria-valuenow', String(activeMax));
    maxThumb.style.left = `${maxPercent}%`;

    trackWrapper.append(track, fill, minThumb, maxThumb);

    // Mirror price-range-slider.js: write '' at an extreme (min at the floor, max at the
    // ceiling) so an untouched slider reads as "no price constraint". #readDom() treats a
    // both-empty range as inactive and reconstructs 0 / data-range-max from the empties, so
    // this keeps the phantom "$0–max" pill out of the applied filters when the shopper never
    // dragged a thumb.
    const minInput = document.createElement('input');
    minInput.type = 'hidden';
    minInput.setAttribute('ref', 'minInput');
    minInput.setAttribute('data-dy-range-min', '');
    minInput.value = activeMin > 0 ? String(activeMin) : '';

    const maxInput = document.createElement('input');
    maxInput.type = 'hidden';
    maxInput.setAttribute('ref', 'maxInput');
    maxInput.setAttribute('data-dy-range-max', '');
    maxInput.value = activeMax < rangeMaxNumber ? String(activeMax) : '';

    slider.append(tmpl, labels, trackWrapper, minInput, maxInput);
    return slider;
  }

  /**
   * Refresh a range panel's bounds from a new DY response without rebuilding the slider.
   * Calls the slider's `update()` method so thumb positions and ARIA values stay accurate.
   * @param {Element} panel
   * @param {any} facet
   */
  #updateRangePanel(panel, facet) {
    const slider = panel.querySelector('price-range-slider-component');
    if (!(slider instanceof HTMLElement)) return;
    const newMax = Number(facet.max ?? 0);
    slider.dataset.rangeMax = String(newMax);
    if (typeof /** @type {any} */ (slider).update === 'function') {
      /** @type {any} */ (slider).update(newMax);
    }
  }

  /**
   * Remove a range panel that DY no longer reports a usable spread for (an un-priced feed reporting
   * 0..0, or a filter narrowed to a single price point), rescuing focus first so it never falls to
   * `<body>`. The whole `.facets__item` wrapper is removed — not just the inner `<details>` — because
   * a stranded empty wrapper keeps its `::before` separator border.
   * @param {Element} existing the inner `<details data-dy-facet-panel>` element being torn down
   * @param {HTMLElement} targetList the `.facets__inner` list the panel lives in
   */
  #teardownStalePanel(existing, targetList) {
    if (existing.contains(document.activeElement)) {
      // Shopper had focus inside this panel (e.g. dragged the price slider into a single-price
      // result); move it to a sibling facet summary so it doesn't fall to <body> on removal.
      // Prefer the next-sibling wrapper first, then the previous-sibling, then any other panel
      // in the list — jumping to the first panel by default (querySelectorAll's document order)
      // disorients a shopper whose focus was several groups down the sidebar.
      const wrapper = existing.closest('.facets__item');
      const nextSummary = wrapper?.nextElementSibling?.querySelector('[data-dy-facet-panel] summary');
      const prevSummary = wrapper?.previousElementSibling?.querySelector('[data-dy-facet-panel] summary');
      const anySummary = [...targetList.querySelectorAll('[data-dy-facet-panel] summary')].find(
        (s) => !existing.contains(s)
      );
      const sibling = nextSummary ?? prevSummary ?? anySummary;
      if (sibling instanceof HTMLElement) {
        sibling.focus();
      } else {
        // No other facet panel to receive focus — sink it into the list container so it still
        // doesn't fall to <body> when the panel is removed below.
        targetList.setAttribute('tabindex', '-1');
        targetList.focus();
      }
    }
    (existing.closest('.facets__item') ?? existing).remove();
  }

  /** @returns {{ filters: Record<string, string[]>, ranges: Record<string, [number, number]>, sortValue: string }} */
  #readUrl() {
    const params = new URLSearchParams(window.location.search);
    /** @type {Record<string, string[]>} */
    const filters = {};
    /** @type {Record<string, [number, number]>} */
    const ranges = {};
    let sortValue = 'relevance';
    for (const [key, raw] of params) {
      if (key === 'dysort') {
        sortValue = raw;
      } else if (key.startsWith('dyf_')) {
        const values = raw.split(',').map((v) => v.trim()).filter(Boolean);
        if (values.length) filters[key.slice(4)] = values;
      } else if (key.startsWith('dyfr_')) {
        let [min, max] = raw.split(':').map(Number);
        if (Number.isFinite(min) && Number.isFinite(max)) {
          // Guard a hand-edited/inverted URL (e.g. dyfr_price=500:100) so we never forward an
          // upside-down range to DY.
          if (min > max) [min, max] = [max, min];
          ranges[key.slice(5)] = [min, max];
        }
      }
    }
    return { filters, ranges, sortValue };
  }

  /**
   * Mirror the active selection into the URL (own `dyf_*` / `dyfr_*` / `dysort` params;
   * leave `q` and everything else untouched) so the view is shareable and back/forward work.
   * Only called on a live filter/sort CHANGE (#dispatchRequest), never on the initial URL restore —
   * so dropping `page` here resets pagination to page 1 on every change, mirroring the grid's own
   * reset, without wiping the page a shopper refreshed on.
   * @param {{ filters: Record<string, string[]>, ranges: Record<string, [number, number]>, sort: { field: string, order: 'asc' | 'desc' } | null }} state
   */
  #syncUrl(state) {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    for (const key of [...params.keys()]) {
      if (key.startsWith('dyf_') || key.startsWith('dyfr_') || key === 'dysort') params.delete(key);
    }
    // A filter/sort change re-queries from page 1, so drop any lingering page param (the grid's
    // numbered pager re-adds it via replaceState when the shopper navigates pages).
    params.delete('page');
    for (const [field, values] of Object.entries(state.filters)) {
      if (values.length) params.set(`dyf_${field}`, values.join(','));
    }
    for (const [field, [min, max]] of Object.entries(state.ranges)) {
      params.set(`dyfr_${field}`, `${min}:${max}`);
    }
    const sortValue = /** @type {HTMLInputElement | null} */ (
      document.querySelector('[data-dy-sort-input]:checked')
    )?.value;
    if (sortValue && sortValue !== 'relevance') params.set('dysort', sortValue);
    history.replaceState(history.state, '', url.toString());
  }

  /** @param {string} text */
  #titleCase(text) {
    return text.replace(/\b\w/g, (char) => char.toUpperCase());
  }
}

if (!customElements.get('dy-search-facets')) {
  customElements.define('dy-search-facets', DySearchFacets);
}
